
import React, { useState, useMemo, useEffect } from 'react';
import { kycService, KycStatusResponse } from '../../services/kyc.service';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import Select from '../../components/common/Select';
import FileUpload from '../../components/common/FileUpload';
import {
  ShieldCheck,
  User as UserIcon,
  Mail,
  Calendar,
  Briefcase,
  BadgeCheck,
  Fingerprint,
  Clock,
  AlertCircle,
  FileSearch,
  Lock,
  Eye,
  Loader2,
  X
} from 'lucide-react';

const Profile: React.FC = () => {
  const { user } = useAuth();
  const { showToast } = useToast();

  const [kycData, setKycData] = useState<KycStatusResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  // Form State
  const [btcAddress, setBtcAddress] = useState('');
  const [docType, setDocType] = useState('PASSPORT');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  // Preview State
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewType, setPreviewType] = useState<'IMAGE' | 'PDF'>('IMAGE');
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    fetchKycStatus();
  }, []);

  const fetchKycStatus = async () => {
    setIsLoading(true);
    const response = await kycService.getKycStatus();
    if (response.success) {
      setKycData(response);
    } else {
      showToast(response.message || 'Failed to fetch KYC protocols', 'error');
    }
    setIsLoading(false);
  };

  const validateBTC = (addr: string) => {
    const regex = /^(1|3|bc1)[a-zA-HJ-NP-Z0-9]{25,62}$/;
    return regex.test(addr);
  };

  const isFormValid = useMemo(() => {
    return (
      validateBTC(btcAddress) &&
      selectedFile !== null
    );
  }, [btcAddress, selectedFile]);

  const handleSubmitKyc = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isFormValid) {
      setFormError('Please complete all security protocols correctly.');
      return;
    }

    setIsSubmitting(true);
    setFormError(null);

    // STEP 1: Upload Document
    setIsUploading(true);
    const uploadRes = await kycService.uploadDocument(selectedFile!);
    setIsUploading(false);

    if (!uploadRes.success) {
      showToast(uploadRes.message || 'Document upload failed. Try again.', 'error');
      setIsSubmitting(false);
      return;
    }

    // STEP 2: Submit KYC
    const submitRes = await kycService.submitKyc({
      document_type: docType,
      btc_address: btcAddress,
      file_key: uploadRes.key
    });

    if (submitRes.success) {
      showToast('Identity verification initiated successfully.', 'success');
      fetchKycStatus();
    } else {
      showToast(submitRes.message || 'Submission failed. Please check your data.', 'error');
    }

    setIsSubmitting(false);
  };

  const handlePreview = async () => {
    setIsPreviewLoading(true);
    try {
      // STEP 1: Fetch KYC Details
      const kycRes = await kycService.getMyKyc();

      // Scenario A: API returns URL directly (as clarified by user)
      if (kycRes.url) {
        setPreviewUrl(kycRes.url);
        // Detect type from URL path (ignoring query params)
        const isPdf = kycRes.url.toLowerCase().split('?')[0].endsWith('.pdf');
        setPreviewType(isPdf ? 'PDF' : 'IMAGE');
        setShowPreview(true);
        return;
      }

      // Scenario B: Two-step handshake (Previous protocol)
      if (kycRes.data?.documentKey) {
        const fileRes = await kycService.getFilePreview(kycRes.data.documentKey);
        if (fileRes.url) {
          setPreviewUrl(fileRes.url);
          setPreviewType(kycRes.data.documentKey.toLowerCase().endsWith('.pdf') ? 'PDF' : 'IMAGE');
          setShowPreview(true);
          return;
        }
      }

      showToast('No submitted proof found for this entity.', 'error');
    } catch (err) {
      showToast('Nexus error during file retrieval. Please try again.', 'error');
    } finally {
      setIsPreviewLoading(false);
    }
  };

  const getStatusDisplay = () => {
    const status = kycData?.status || 'NOT_SUBMITTED';
    switch (status) {
      case 'PENDING':
        return {
          color: 'text-brand-primary bg-brand-primary/10 border-brand-primary/20',
          label: 'Verification Pending',
          icon: <Clock size={16} />,
          desc: 'Your identity documents are currently under automated review by the Nexus Compliance Protocol. Expect clearance within 24-48 hours.'
        };
      case 'APPROVED':
        return {
          color: 'text-emerald-500 bg-emerald-500/10 border-emerald-500/20',
          label: 'Verified Identity',
          icon: <BadgeCheck size={16} />,
          desc: 'Your profile is fully verified. All trade limits and capital redemption channels are unrestricted.'
        };
      case 'REJECTED':
        return {
          color: 'text-red-500 bg-red-500/10 border-red-500/20',
          label: 'Verification Rejected',
          icon: <AlertCircle size={16} />,
          desc: 'Your identity verification was rejected. Please review your documents and resubmit the protocol.'
        };
      case 'NOT_SUBMITTED':
      default:
        return {
          color: 'text-brand-muted bg-brand-border',
          label: 'KYC Not Submitted',
          icon: <FileSearch size={16} />,
          desc: 'To unlock high-volume trading and withdrawals, you must complete the Identity Protocol below.'
        };
    }
  };

  const statusInfo = getStatusDisplay();

  if (isLoading) {
    return (
      <div className="h-[60vh] flex items-center justify-center">
        <Loader2 className="animate-spin text-brand-primary" size={48} />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-700">

      {/* Header Info */}
      <section className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="space-y-1">
          <h1 className="text-3xl font-bold text-white tracking-tight uppercase">Identity Hub</h1>
          <p className="text-brand-muted text-xs font-bold uppercase tracking-widest opacity-70 flex items-center gap-2">
            <Lock size={12} className="text-brand-primary" />
            End-to-end encrypted compliance terminal
          </p>
        </div>
        <div className={`px-4 py-2 rounded-xl border flex items-center gap-2 text-xs font-bold uppercase tracking-widest ${statusInfo.color}`}>
          {statusInfo.icon}
          {statusInfo.label}
        </div>
      </section>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">

        {/* Left Column: Profile Card */}
        <div className="lg:col-span-5 space-y-6">
          <div className="bg-brand-card border border-brand-border rounded-3xl overflow-hidden shadow-2xl">
            <div className="h-24 bg-gradient-to-r from-brand-primary/20 to-brand-dark border-b border-brand-border"></div>
            <div className="px-8 pb-8 -mt-12 relative">
              <div className="w-24 h-24 rounded-2xl bg-brand-card border-4 border-brand-dark overflow-hidden shadow-lg mb-4">
                <div className="w-full h-full bg-brand-dark border border-brand-border flex items-center justify-center text-brand-primary text-3xl font-black">
                  {user?.name?.charAt(0) || 'U'}
                </div>
              </div>

              <div className="space-y-6">
                <div>
                  <h3 className="text-2xl font-bold text-white tracking-tight truncate">{user?.name}</h3>
                  <div className="flex items-center gap-2 text-brand-primary text-[10px] font-bold uppercase tracking-[0.2em] mt-1">
                    <Fingerprint size={12} />
                    {kycData?.status === 'APPROVED' ? 'Tier 1 Verified Entity' : 'Unverified Participant'}
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-4 py-4 border-y border-brand-border/30">
                  <ProfileItem icon={<Mail size={14} />} label="Email Address" value={user?.email || 'N/A'} />
                  <ProfileItem icon={<Briefcase size={14} />} label="Account Authority" value={user?.role || 'TRADER'} />
                  <ProfileItem icon={<ShieldCheck size={14} />} label="Compliance Status" value={kycData?.status || 'NOT_SUBMITTED'} />
                  <ProfileItem icon={<Clock size={14} />} label="Protocol Timestamp" value={kycData?.submittedAt ? new Date(kycData.submittedAt).toLocaleDateString() : 'NO UPLINK DETECTED'} />
                </div>

                <div className="bg-brand-primary/5 border border-brand-primary/10 p-4 rounded-2xl flex items-start gap-3">
                  <AlertCircle size={16} className="text-brand-primary mt-0.5 shrink-0" />
                  <p className="text-[10px] text-brand-muted leading-relaxed uppercase font-bold tracking-wider">
                    Security Note: Profile changes require a 2FA hardware key or manual nexus audit.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column: KYC Form or Status Banner */}
        <div className="lg:col-span-7 space-y-6">
          {kycData?.status === 'NOT_SUBMITTED' || kycData?.status === 'REJECTED' ? (
            <div className="bg-brand-card border border-brand-border rounded-3xl p-8 shadow-xl">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-10 rounded-xl bg-brand-primary/10 flex items-center justify-center text-brand-primary">
                  <ShieldCheck size={24} />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white uppercase tracking-tight">KYC Wallet Protocol</h2>
                  <p className="text-xs text-brand-muted font-medium">Verify your financial identity for unrestricted access.</p>
                </div>
              </div>

              <form onSubmit={handleSubmitKyc} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Input
                    label="Wallet Holder Name"
                    placeholder="Enter full legal name"
                    value={user?.name || ''}
                    disabled
                  />
                  <Select
                    label="Document Protocol"
                    options={[
                      { value: 'PASSPORT', label: 'International Passport' },
                      { value: 'AADHAAR', label: 'Aadhaar Card' },
                      { value: 'LICENSE', label: 'Driving License' }
                    ]}
                    value={docType}
                    onChange={(e) => setDocType(e.target.value)}
                  />
                </div>

                <Input
                  label="BTC Settlement Address"
                  placeholder="bc1q..."
                  value={btcAddress}
                  onChange={(e) => setBtcAddress(e.target.value)}
                  error={btcAddress && !validateBTC(btcAddress) ? 'Invalid BTC protocol detected' : undefined}
                />

                <FileUpload
                  label="Proof of Identity (Digital Scan)"
                  fileName={selectedFile?.name || null}
                  onFileSelect={(file) => {
                    const validTypes = ['image/jpeg', 'image/png', 'image/jpg'];
                    const maxSize = 5 * 1024 * 1024; // 5MB

                    if (!validTypes.includes(file.type)) {
                      showToast('Invalid file protocol. Please upload JPG or PNG images only.', 'error');
                      return;
                    }

                    if (file.size > maxSize) {
                      showToast('Security Violation: File size exceeds 5MB limit.', 'error');
                      return;
                    }

                    setSelectedFile(file);
                    setFormError(null);
                  }}
                  error={formError && !selectedFile ? 'Document scan is mandatory' : undefined}
                />

                <div className="pt-2">
                  <Button
                    type="submit"
                    className="w-full h-14"
                    disabled={!isFormValid || isSubmitting}
                    isLoading={isSubmitting}
                  >
                    {isUploading ? 'UPLOADING SCAN...' : 'INITIATE VERIFICATION'}
                  </Button>
                  {formError && <p className="text-center text-red-400 text-[10px] font-bold uppercase tracking-widest mt-4">{formError}</p>}
                </div>
              </form>
            </div>
          ) : (
            <div className="bg-brand-card border border-brand-border rounded-3xl p-12 text-center space-y-6 shadow-xl relative overflow-hidden group">
              <div className="absolute inset-0 bg-brand-primary/5 opacity-0 group-hover:opacity-100 transition-opacity"></div>

              <div className="w-20 h-20 rounded-full bg-brand-primary/10 flex items-center justify-center text-brand-primary mx-auto border border-brand-primary/20 animate-pulse">
                {statusInfo.icon}
              </div>

              <div className="space-y-2 relative z-10">
                <h2 className="text-2xl font-bold text-white uppercase tracking-tight">{statusInfo.label}</h2>
                <p className="text-brand-muted text-sm max-w-sm mx-auto leading-relaxed">
                  {statusInfo.desc}
                </p>
              </div>

              {kycData?.documentType && (
                <div className="pt-6">
                  <Button
                    onClick={handlePreview}
                    isLoading={isPreviewLoading}
                    variant="secondary"
                    className="h-12 px-8 text-[10px] tracking-[0.2em] font-black"
                  >
                    <Eye size={14} className="mr-2" /> VIEW SUBMITTED PROOF
                  </Button>
                </div>
              )}

              <div className="pt-4">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-brand-dark border border-brand-border rounded-xl text-[10px] font-bold text-brand-muted uppercase tracking-[0.2em]">
                  <ShieldCheck size={12} className="text-emerald-500" />
                  Assets remain secure under NEXUS protection
                </div>
              </div>
            </div>
          )}

          {/* Compliance Guidelines */}
          <div className="bg-brand-dark/40 border border-brand-border p-6 rounded-2xl space-y-4">
            <h4 className="text-[10px] font-bold text-brand-muted uppercase tracking-[0.2em]">Nexus Compliance Guidelines</h4>
            <ul className="space-y-3">
              {[
                'Ensure the digital scan is high-resolution and non-obfuscated.',
                'The BTC address must match your primary settlement destination.',
                'Identity verification is a one-time protocol for Tier 1 clearance.'
              ].map((text, i) => (
                <li key={i} className="flex items-start gap-3">
                  <div className="w-1 h-1 rounded-full bg-brand-primary mt-1.5"></div>
                  <p className="text-[10px] text-brand-muted font-medium uppercase tracking-wider leading-relaxed">{text}</p>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
      {/* Secure Preview Modal */}
      {showPreview && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center p-6 bg-brand-dark/95 backdrop-blur-md animate-in fade-in duration-300">
          <div className="bg-brand-card border border-brand-border w-full max-w-4xl rounded-3xl overflow-hidden shadow-[0_0_50px_rgba(0,0,0,0.8)] animate-in zoom-in-95 duration-300 flex flex-col max-h-[90vh]">
            <div className="p-6 border-b border-brand-border flex items-center justify-between bg-brand-dark/40">
              <div>
                <h3 className="text-white font-bold uppercase tracking-tight">Encrypted Proof Preview</h3>
                <p className="text-[10px] text-brand-muted uppercase font-bold tracking-widest mt-0.5">Secure terminal access to submitted identity documents</p>
              </div>
              <button
                onClick={() => { setShowPreview(false); setPreviewUrl(null); }}
                className="p-2 rounded-xl bg-brand-dark hover:bg-brand-border text-brand-muted hover:text-white transition-all"
              >
                <X size={20} />
              </button>
            </div>

            <div className="flex-1 overflow-auto p-4 bg-black/20 flex items-center justify-center">
              {previewUrl ? (
                previewType === 'PDF' ? (
                  <iframe
                    src={previewUrl}
                    className="w-full h-full min-h-[60vh] rounded-xl border-0"
                    title="Submitted Proof"
                  />
                ) : (
                  <img
                    src={previewUrl}
                    alt="Submitted Proof"
                    className="max-w-full max-h-full object-contain rounded-xl shadow-2xl"
                  />
                )
              ) : (
                <div className="flex flex-col items-center gap-4 text-brand-muted">
                  <Loader2 className="animate-spin text-brand-primary" size={32} />
                  <p className="text-[10px] font-black uppercase tracking-widest">Decrypting asset...</p>
                </div>
              )}
            </div>

            <div className="p-6 border-t border-brand-border bg-brand-dark/40 flex justify-end">
              <Button
                onClick={() => { setShowPreview(false); setPreviewUrl(null); }}
                variant="secondary"
                className="px-8"
              >
                CLOSE TERMINAL
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const ProfileItem: React.FC<{ icon: React.ReactNode, label: string, value: string }> = ({ icon, label, value }) => (
  <div className="flex items-start gap-3">
    <div className="text-brand-primary mt-1 opacity-70">{icon}</div>
    <div>
      <p className="text-[9px] text-brand-muted uppercase font-bold tracking-[0.15em] mb-0.5">{label}</p>
      <p className="text-xs text-white font-bold tracking-tight">{value}</p>
    </div>
  </div>
);

export default Profile;
